
import React, { useEffect, useRef, Suspense } from 'react';
import { User, SiteTheme, HeroLayoutType } from '../../types';
import { Loader2 } from 'lucide-react';

// Lazy load variants for performance
const HeroCard = React.lazy(() => import('./hero/variants/Card').then(module => ({ default: module.HeroCard })));
const HeroSplit = React.lazy(() => import('./hero/variants/Split').then(module => ({ default: module.HeroSplit })));
const HeroFade = React.lazy(() => import('./hero/variants/Fade').then(module => ({ default: module.HeroFade })));
const HeroBanner = React.lazy(() => import('./hero/variants/Banner').then(module => ({ default: module.HeroBanner })));
const HeroEditorial = React.lazy(() => import('./hero/variants/Editorial').then(module => ({ default: module.HeroEditorial })));
const HeroVideo = React.lazy(() => import('./hero/variants/Video').then(module => ({ default: module.HeroVideo })));

interface StoreHeroProps {
  student?: User;
  className?: string; // New: Class name passed from StoreFront
  title?: string;
  text?: string;
  imageUrl?: string;
  mobileImageUrl?: string; 
  heroVideoUrl?: string; 
  theme?: SiteTheme;
  
  // Design Props
  heroLayout?: HeroLayoutType;
  titleColor?: string;
  textColor?: string;
  shadow?: 'none' | 'soft' | 'hard' | 'strong' | 'glow';
  titleShadow?: 'none' | 'soft' | 'hard' | 'strong' | 'glow';
  textShadow?: 'none' | 'soft' | 'hard' | 'strong';
  
  // Texture & Outline
  heroTitleTexture?: string;
  heroTitleOutlineWidth?: 'none' | 'sm' | 'md' | 'lg';
  heroTitleOutlineColor?: string;
  heroTextOutlineWidth?: 'none' | 'sm' | 'md';
  heroTextOutlineColor?: string;

  subtitleSize?: 'sm' | 'md' | 'lg' | 'xl';
  titleSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'; 
  height?: 'small' | 'medium' | 'large' | 'full';
  
  // Positioning Props
  heroTextAlignment?: 'left' | 'center' | 'right';
  heroContentPosition?: number;
  heroContentPositionY?: number;

  // Button Props
  heroButtonColor?: string;
  heroButtonTextColor?: string;

  scrollContainerId?: string; 
}

export const StoreHero: React.FC<StoreHeroProps> = (props) => {
  const { heroLayout = 'fade', scrollContainerId, shadow, titleShadow, textShadow, titleSize, subtitleSize, height, className } = props;
  
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
      // Added 'split' to the array to enable parallax logic
      if (['card', 'fade', 'banner', 'video', 'split'].includes(heroLayout)) {
          const target = scrollContainerId ? document.getElementById(scrollContainerId) : window;

          // Ingen parallax på pekskärmar eller vid minskad rörelse: på mobilen ritas
          // bildlagrets kant om för sent vid scroll och blir randig mot raden under.
          const media = (q: string) => typeof window.matchMedia === 'function' && window.matchMedia(q).matches;
          if (media('(hover: none), (pointer: coarse)') || media('(prefers-reduced-motion: reduce)')) {
              if (parallaxRef.current) {
                  parallaxRef.current.style.transform = 'none';
                  parallaxRef.current.style.willChange = 'auto';
              }
              return;
          }

          if (parallaxRef.current) {
              parallaxRef.current.style.willChange = 'transform';
          }

          let ticking = false;

          const handleScroll = () => {
              if (!ticking) {
                  window.requestAnimationFrame(() => {
                      if (!parallaxRef.current) return;
                      const y = scrollContainerId && target instanceof HTMLElement 
                          ? target.scrollTop 
                          : window.scrollY;
                      
                      // Heron syns inte längre: ingen anledning att flytta bilden.
                      if (y > window.innerHeight * 1.5) { ticking = false; return; }
                      // Use transform3d for GPU acceleration
                      parallaxRef.current.style.transform = `translate3d(0, ${Math.round(y * 0.4)}px, 0)`;
                      ticking = false;
                  });
                  ticking = true;
              }
          };
          
          target?.addEventListener('scroll', handleScroll, { passive: true });
          return () => target?.removeEventListener('scroll', handleScroll);
      }
  }, [scrollContainerId, heroLayout]);

  const handleScrollToShop = () => {
      document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  // --- HELPER CLASSES ---
  const getShadowClass = (type?: string) => {
      switch (type) {
          case 'none': return '';
          case 'hard': return 'drop-shadow-[2px_2px_0_rgba(0,0,0,0.8)]';
          case 'strong': return 'drop-shadow-xl';
          case 'soft': return 'drop-shadow-md';
          case 'glow': return 'drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]';
          default: return 'drop-shadow-md';
      }
  };
  
  const titleShadowClass = getShadowClass(titleShadow || shadow);
  const textShadowClass = getShadowClass(textShadow || shadow);

  const getTitleSize = (size?: string) => {
        switch (size) {
            case 'sm': return 'text-xl md:text-3xl lg:text-4xl';
            case 'md': return 'text-2xl md:text-4xl lg:text-5xl';
            case 'lg': return 'text-3xl md:text-5xl lg:text-6xl'; 
            case 'xl': return 'text-4xl md:text-6xl lg:text-7xl';
            case '2xl': return 'text-5xl md:text-7xl lg:text-8xl';
            default: return 'text-3xl md:text-5xl lg:text-6xl'; 
        }
  };
  const titleSizeClass = getTitleSize(titleSize || 'lg');

  const getSubtitleSize = (size?: string) => {
      switch (size) {
          case 'sm': return 'text-xs md:text-sm';
          case 'lg': return 'text-base md:text-lg';
          case 'xl': return 'text-lg md:text-xl';
          default: return 'text-sm md:text-base'; 
      }
  };
  const subtitleSizeClass = getSubtitleSize(subtitleSize || 'md');

  const getHeightClass = (h: string) => {
      switch (h) {
          case 'small': return 'min-h-[35vh] md:min-h-[50vh]';
          case 'large': return 'min-h-[60vh] md:min-h-[80vh]';
          case 'full': return 'min-h-[80vh] md:min-h-screen';
          case 'medium': 
          default: return 'min-h-[45vh] md:min-h-[60vh]';
      }
  };
  const heightClass = getHeightClass(height || 'medium');

  const commonProps = {
      ...props,
      titleShadowClass,
      textShadowClass,
      titleSizeClass,
      subtitleSizeClass,
      heightClass,
      // Säljare och grupp visas i raden "Du stödjer" under heron, inte här.
      student: undefined,
      className: undefined,
      onScrollToShop: handleScrollToShop,
      parallaxRef,
      heroTextAlignment: props.heroTextAlignment || 'center',
      heroContentPosition: (props.heroContentPosition !== undefined && props.heroContentPosition !== null) ? props.heroContentPosition : 50,
      heroContentPositionY: (props.heroContentPositionY !== undefined && props.heroContentPositionY !== null) ? props.heroContentPositionY : 50,
      heroButtonColor: props.heroButtonColor,
      heroButtonTextColor: props.heroButtonTextColor,
      heroTitleTexture: props.heroTitleTexture,
      heroTitleOutlineWidth: props.heroTitleOutlineWidth,
      heroTitleOutlineColor: props.heroTitleOutlineColor,
      heroTextOutlineWidth: props.heroTextOutlineWidth,
      heroTextOutlineColor: props.heroTextOutlineColor,
      videoUrl: props.heroVideoUrl, // Pass video URL
  };

  return (
      <Suspense fallback={<div className={`w-full ${heightClass} bg-stone-100 flex items-center justify-center`}><Loader2 className="animate-spin text-stone-300" /></div>}>
          {heroLayout === 'split' && <HeroSplit {...commonProps} />}
          {heroLayout === 'fade' && <HeroFade {...commonProps} />}
          {heroLayout === 'banner' && <HeroBanner {...commonProps} />}
          {heroLayout === 'editorial' && <HeroEditorial {...commonProps} />}
          {heroLayout === 'video' && <HeroVideo {...commonProps} />}
          {heroLayout === 'card' && <HeroCard {...commonProps} />}
      </Suspense>
  );
};
